/**
 * Canvas interactif pour l'édition visuelle des étiquettes
 * Affiche les champs avec drag-and-drop et redimensionnement
 */

import { useRef, useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import { FieldConfig } from '../../types/label-templates.types';

interface Props {
  widthMm: number;
  heightMm: number;
  dpi: number;
  fields: FieldConfig[];
  selectedFieldId: string | null;
  showGrid: boolean;
  scale: number;
  onFieldClick: (fieldId: string) => void;
  onFieldMove: (fieldId: string, x: number, y: number) => void;
  onFieldAdd: (fieldId: string, x: number, y: number) => void;
}

const PIXELS_PER_MM = 4; // 1mm = 4px pour affichage confortable

/**
 * Convertit une taille de police (pt) en pixels pour l'affichage
 * Utilise la même formule que le générateur ZPL : fontSize * DPI / 72
 * puis convertit les dots en pixels d'affichage
 */
function fontSizeToDisplayPx(fontSize: number, dpi: number, scale: number): number {
  // Formule ZPL : hauteur_dots = fontSize * dpi / 72
  const heightDots = (fontSize * dpi) / 72;
  // Convertir dots en mm : dots / (dpi / 25.4)
  const heightMm = heightDots / (dpi / 25.4);
  // Convertir mm en pixels d'affichage
  return Math.max(10, heightMm * PIXELS_PER_MM * scale);
}

export function LabelCanvas({
  widthMm,
  heightMm,
  dpi,
  fields,
  selectedFieldId,
  showGrid,
  scale,
  onFieldClick,
  onFieldMove,
  onFieldAdd,
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const widthPx = widthMm * PIXELS_PER_MM * scale;
  const heightPx = heightMm * PIXELS_PER_MM * scale;

  // Zone de drop pour ajouter de nouveaux champs
  const [{ isOver }, drop] = useDrop({
    accept: 'NEW_FIELD',
    drop: (item: { fieldId: string }, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = (offset.x - rect.left) / (PIXELS_PER_MM * scale);
      const y = (offset.y - rect.top) / (PIXELS_PER_MM * scale);

      onFieldAdd(item.fieldId, Math.max(0, x), Math.max(0, y));
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div className="relative bg-white rounded-lg border-2 border-gray-300 overflow-auto p-8">
      {/* Règles */}
      <div className="flex">
        {/* Règle horizontale */}
        <div className="ml-8 mb-2" style={{ width: widthPx }}>
          <svg width={widthPx} height="20" className="border-b border-gray-300">
            {Array.from({ length: Math.ceil(widthMm) }, (_, i) => (
              <g key={i}>
                <line
                  x1={i * PIXELS_PER_MM * scale}
                  y1="15"
                  x2={i * PIXELS_PER_MM * scale}
                  y2="20"
                  stroke="#666"
                  strokeWidth="1"
                />
                <text
                  x={i * PIXELS_PER_MM * scale + 2}
                  y="12"
                  fontSize="9"
                  fill="#666"
                >
                  {i}
                </text>
              </g>
            ))}
          </svg>
        </div>
      </div>

      <div className="flex">
        {/* Règle verticale */}
        <div className="mr-2" style={{ height: heightPx }}>
          <svg width="30" height={heightPx} className="border-r border-gray-300">
            {Array.from({ length: Math.ceil(heightMm) }, (_, i) => (
              <g key={i}>
                <line
                  x1="25"
                  y1={i * PIXELS_PER_MM * scale}
                  x2="30"
                  y2={i * PIXELS_PER_MM * scale}
                  stroke="#666"
                  strokeWidth="1"
                />
                <text
                  x="2"
                  y={i * PIXELS_PER_MM * scale + 10}
                  fontSize="9"
                  fill="#666"
                >
                  {i}
                </text>
              </g>
            ))}
          </svg>
        </div>

        {/* Canvas principal */}
        <div
          ref={(node) => {
            // @ts-ignore - callback ref pattern
            canvasRef.current = node;
            drop(node);
          }}
          className={`relative bg-white border-2 ${
            isOver ? 'border-indigo-500 bg-indigo-50' : 'border-gray-400'
          }`}
          style={{
            width: widthPx,
            height: heightPx,
            backgroundImage: showGrid
              ? `repeating-linear-gradient(
                  0deg,
                  #e5e7eb 0,
                  #e5e7eb 1px,
                  transparent 1px,
                  transparent ${PIXELS_PER_MM * scale}px
                ),
                repeating-linear-gradient(
                  90deg,
                  #e5e7eb 0,
                  #e5e7eb 1px,
                  transparent 1px,
                  transparent ${PIXELS_PER_MM * scale}px
                )`
              : 'none',
          }}
        >
          {/* Rendu des champs */}
          {fields
            .sort((a, b) => a.zIndex - b.zIndex)
            .map((field) => (
              <DraggableField
                key={field.id}
                field={field}
                isSelected={field.id === selectedFieldId}
                scale={scale}
                dpi={dpi}
                pixelsPerMm={PIXELS_PER_MM}
                onClick={() => onFieldClick(field.id)}
                onMove={(x, y) => onFieldMove(field.id, x, y)}
              />
            ))}

          {/* Indicateur de drop */}
          {isOver && (
            <div className="absolute inset-0 bg-indigo-100/50 pointer-events-none flex items-center justify-center">
              <span className="text-indigo-600 font-medium">
                Déposez le champ ici
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Légende */}
      <div className="mt-4 text-xs text-gray-500 flex items-center gap-4">
        <span>Dimensions: {widthMm} × {heightMm} mm</span>
        <span>Échelle: {Math.round(scale * 100)}%</span>
        <span>Champs actifs: {fields.filter((f) => f.enabled).length}</span>
      </div>
    </div>
  );
}

interface DraggableFieldProps {
  field: FieldConfig;
  isSelected: boolean;
  scale: number;
  dpi: number;
  pixelsPerMm: number;
  onClick: () => void;
  onMove: (x: number, y: number) => void;
}

function DraggableField({
  field,
  isSelected,
  scale,
  dpi,
  pixelsPerMm,
  onClick,
  onMove,
}: DraggableFieldProps) {
  const dragRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0, fieldX: 0, fieldY: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Bouton gauche uniquement
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      fieldX: field.x,
      fieldY: field.y,
    };
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = (e.clientX - dragStartPos.current.x) / (pixelsPerMm * scale);
      const deltaY = (e.clientY - dragStartPos.current.y) / (pixelsPerMm * scale);

      const newX = Math.max(0, dragStartPos.current.fieldX + deltaX);
      const newY = Math.max(0, dragStartPos.current.fieldY + deltaY);

      onMove(newX, newY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, pixelsPerMm, scale, onMove]);

  const xPx = field.x * pixelsPerMm * scale;
  const yPx = field.y * pixelsPerMm * scale;

  // Rendu selon le type de champ
  let content: React.ReactNode;
  let widthPx = 100;
  let heightPx = 30;

  if (field.type === 'qr') {
    const size = (field.qrSize || 15) * pixelsPerMm * scale;
    widthPx = size;
    heightPx = size;
    content = (
      <div className="w-full h-full bg-gray-800 flex items-center justify-center text-white text-xs">
        QR
      </div>
    );
  } else if (field.type === 'barcode') {
    widthPx = 80 * scale;
    heightPx = (field.barcodeHeight || 10) * pixelsPerMm * scale;
    content = (
      <div className="w-full h-full bg-gray-800 flex items-center justify-center text-white text-xs">
        |||||||
      </div>
    );
  } else if (field.type === 'badge') {
    widthPx = 60 * scale;
    heightPx = 24 * scale;
    content = (
      <div className="w-full h-full bg-black text-white flex items-center justify-center text-xs font-bold">
        {field.label?.toUpperCase() || field.id.toUpperCase()}
      </div>
    );
  } else {
    // Texte - utiliser la même formule que le générateur ZPL
    const fontSize = fontSizeToDisplayPx(field.fontSize || 10, dpi, scale);
    widthPx = Math.max(100 * scale, fontSize * 6);
    heightPx = fontSize * 1.3;

    // Afficher le texte libre ou un placeholder
    let displayText = field.label || field.id;

    // Champs de texte libre
    if (field.source === 'freetext') {
      displayText = field.freeText || 'Cliquez pour saisir le texte';
    }
    // Champs de prix avec exemple
    else if (field.source === 'computed' && (field.id.includes('price') || field.id === 'product_price')) {
      const exampleValue = '2.50';
      const suffix = field.suffix || '€';
      displayText = `${field.label} (ex: ${exampleValue}${suffix})`;
    }
    // Ajouter préfixe et suffixe si définis (pour les autres champs)
    else {
      if (field.prefix) {
        displayText = field.prefix + displayText;
      }
      if (field.suffix) {
        displayText = displayText + field.suffix;
      }
    }

    content = (
      <div
        style={{
          fontSize: fontSize,
          fontWeight: field.fontWeight || 'normal',
          textAlign: field.align || 'left',
        }}
        className="truncate"
      >
        {displayText}
      </div>
    );
  }

  return (
    <div
      ref={dragRef}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`absolute cursor-move transition-shadow ${
        isSelected
          ? 'ring-2 ring-indigo-500 shadow-lg'
          : 'ring-1 ring-gray-300 hover:ring-indigo-300'
      } ${isDragging ? 'opacity-70' : field.enabled ? 'opacity-100' : 'opacity-40'}`}
      style={{
        left: xPx,
        top: yPx,
        width: widthPx,
        height: heightPx,
        backgroundColor: field.enabled ? 'white' : '#f9fafb',
        borderRadius: '2px',
        padding: '2px 4px',
        borderStyle: field.enabled ? 'solid' : 'dashed',
      }}
    >
      {content}

      {/* Indicateur "désactivé" */}
      {!field.enabled && (
        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1 rounded-bl" style={{ fontSize: '8px' }}>
          OFF
        </div>
      )}

      {/* Poignées de redimensionnement (optionnel pour v2) */}
      {isSelected && (
        <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-indigo-500 rounded-full cursor-se-resize" />
      )}
    </div>
  );
}
